import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs, { Dayjs } from "dayjs";
import { unwrapResult } from "@reduxjs/toolkit";
import { styled } from "@mui/material";
import { toast } from "react-toastify";

import { InfoRow as Row } from "../../../../components/StyledComponents";
import { dateNormalizer, errorNormalizer } from "../../../../shared/Helpers/functions";
// import ActivateSession from "../../../Calendar/Actions/activateEvent";
import { CustomButton, Picker, Text } from "../../../../shared/uiComponents";
import { useResetCalendarData } from "../../Helpers/hooks";
import { AdminTypes } from "../../../../components/Menu";

import { DispatchProperties, useSelector } from "../../../../redux/store";
import { UpdateSessionProperties } from "../../../../redux/API/ClientAPIHelpers/sessionsProperties";
import { updateSession } from "../../../../redux/State/clientSlice/sessionSlice";
import { isPastTime } from "../../Helpers/functions";

export const SessionInfoButtonsWrapper = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "8px 0",
  margin: "auto",
  alignItems: "center",
  lineHeight: "24px",
  gap: "16px",
  ">div": {
    wordWrap: "break-word",
    flex: "1 0 0",
  },
  "@media (max-width: 425px)": {
    flexDirection: "column",
    ">div": {
      width: "100%",
      marginBottom: "16px",
      marginRight: 0,
    },
  },
}));

const SessionDate = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const { resetData } = useResetCalendarData();

  const loading = useSelector((state) => state.session.loading);
  const role = useSelector((state) => state.account.role);
  const data = useSelector((state) => state.calendar.infoEventData);
  const roleRestriction = useSelector((state) => state.account.roleRestriction);

  const [date, setDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [isAllowed, setIsAllowed] = useState<boolean>(false);

  const isAdmin = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs;

  const dateChangeHandler = (value: Dayjs | null) => {
    setDate(value);
  };

  const startTimeChangeHandler = (value: Dayjs | null) => {
    setStartTime(value);
  };

  const endTimeChangeHandler = (value: Dayjs | null) => {
    setEndTime(value);
  };

  const saveHandler = () => {
    if (!data || !date || !endTime || !startTime) return;
    const sessionId = data.sessionId;
    const newDate = dateNormalizer(date);

    let updatedSession: UpdateSessionProperties = {
      id: sessionId,
      date: newDate,
      endTime: endTime.format("YYYY-MM-DDTHH:mm"),
      startTime: startTime.format("YYYY-MM-DDTHH:mm"),
    };
    if (!date.isSame(startTime, "date") || !date.isSame(endTime, "date")) {
      updatedSession = {
        id: sessionId,
        date: newDate,
        endTime: `${newDate}T${endTime.format("HH:mm")}`,
        startTime: `${newDate}T${startTime.format("HH:mm")}`,
      };
    }

    dispatch(updateSession({ sessionId, data: updatedSession }))
      .then(unwrapResult)
      .then(() => {
        resetData();
        toast("Success");
      })
      .catch(errorNormalizer);
  };

  useEffect(() => {
    if (!roleRestriction) {
      setIsAllowed(true);
      return;
    }
    const today = new Date();
    const allowedAfterDate = date?.toDate();
    if (!allowedAfterDate) return;

    allowedAfterDate.setDate(allowedAfterDate.getDate() + roleRestriction);
    const isPast = isPastTime(today, allowedAfterDate);
    setIsAllowed(!isPast && isAdmin);
  }, [date, isAdmin, roleRestriction]);

  useEffect(() => {
    if (!data) return;
    const date = dayjs(data.date);
    const startTime = dayjs(data.startTime).utc(false);
    const endTime = dayjs(data.endTime).utc(false);
    setDate(date);
    setStartTime(startTime);
    setEndTime(endTime);
  }, [data]);

  return (
    <>
      <Row>
        <Text title={"Date"} size={"smallBold"} />
        <Picker.CustomDate
          value={date}
          onChange={dateChangeHandler}
          label=""
          disabled={role?.section.id !== AdminTypes.admin || !data?.sessionId}
        />
      </Row>
      <Row>
        <Text title={"Start Time"} size={"smallBold"} />
        <Picker.CustomTime
          value={startTime}
          onChange={startTimeChangeHandler}
          label=""
          disabled={!isAllowed || !data?.sessionId}
        />
      </Row>
      <Row>
        <Text title={"End Time"} size={"smallBold"} />
        <Picker.CustomTime
          value={endTime}
          onChange={endTimeChangeHandler}
          label=""
          disabled={!isAllowed || !data?.sessionId}
        />
      </Row>
      <SessionInfoButtonsWrapper>
        {isAllowed && !!data?.sessionId && (
          <CustomButton
            title="Save Changes"
            onClick={saveHandler}
            loading={loading}
            secondaryButton
          />
        )}
        {/* {role?.section.id === AdminTypes.admin && !!data?.sessionId && <ActivateSession />} */}
      </SessionInfoButtonsWrapper>
    </>
  );
};

export default SessionDate;
